import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';

/**
 * Confirms Finance approval of a batch. The server re-validates and may reject
 * with blockers (422), shown inline. `onConfirm` calls the API and throws on
 * failure (error carrying `body.blockers`).
 */
function FinanceApproveDialog({ open, batch, onClose, onConfirm }) {
  const [submitting, setSubmitting] = useState(false);
  const [blockers, setBlockers] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setBlockers([]); setError(''); setSubmitting(false); }
  }, [open]);

  const handleConfirm = async () => {
    setSubmitting(true); setBlockers([]); setError('');
    try {
      await onConfirm();
    } catch (e) {
      if (e.body && Array.isArray(e.body.blockers) && e.body.blockers.length) setBlockers(e.body.blockers);
      else setError(e.message || 'Approval failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Approve batch</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Approving finalizes all non-excluded, non-rejected rows, records the approved totals, and
          locks the batch. This is the final payment decision.
        </DialogContentText>
        {blockers.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>Cannot approve yet — resolve these first:</AlertTitle>
            <List dense disablePadding>
              {blockers.map((b, i) => (
                <ListItem key={i} disableGutters sx={{ py: 0 }}><ListItemText primary={`• ${b}`} /></ListItem>
              ))}
            </List>
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" color="success" onClick={handleConfirm} disabled={submitting}>
          {submitting ? 'Approving…' : 'Approve batch'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FinanceApproveDialog;
